import { useEffect, useState } from "react";
import {
  createDocsPage,
  fetchDocsPage,
  updateDocsPage,
  uploadDocsPageDocx,
} from "@/api/docsSiteApi";

export default function DocsPageEditor({ pageId, onClose, onSaved }) {
  const isNew = !pageId;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("draft");
  const [htmlBody, setHtmlBody] = useState("");
  const [contentFormat, setContentFormat] = useState("html");

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await fetchDocsPage(pageId);
        if (cancelled) return;
        setTitle(page.title || "");
        setSlug(page.slug || "");
        setStatus(page.status || "draft");
        setHtmlBody(page.htmlBody || "");
        setContentFormat(page.contentFormat || "html");
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load page");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, pageId]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        slug: slug.trim(),
        status,
        htmlBody: htmlBody,
        contentFormat: contentFormat,
      };
      if (isNew) {
        await createDocsPage(body);
      } else {
        await updateDocsPage(pageId, body);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDocxUpload(e) {
    const file = e.target.files?.[0];
    if (!file || isNew) return;
    setSaving(true);
    setError(null);
    try {
      const page = await uploadDocsPageDocx(pageId, file);
      setHtmlBody(page.htmlBody || "");
      setContentFormat(page.contentFormat || "docx");
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isNew ? "New page" : "Edit page"}
          </h2>
          <button type="button" className="ui-btn-secondary !px-3 !py-2" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="p-6 text-sm text-slate-500">Loading…</p>
        ) : (
          <form onSubmit={handleSave} className="space-y-4 p-6">
            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
                {error}
              </p>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Title</label>
              <input className="ui-field w-full" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Slug</label>
              <input
                className="ui-field w-full font-mono text-sm"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="getting-started"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Status</label>
              <select className="ui-field w-full" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            {!isNew && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Upload Word (.docx)
                </label>
                <input type="file" accept=".docx" onChange={handleDocxUpload} disabled={saving} />
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">HTML content</label>
              <textarea
                className="ui-field min-h-[200px] w-full font-mono text-sm"
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                placeholder="<p>Your documentation content…</p>"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="ui-btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="ui-btn-primary" disabled={saving}>
                {saving ? "Saving…" : "Save page"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
